import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type {
  OrderPosition,
  SparePartOrder
} from '../api/hau-itde-spareparts-purchase-nonshop-portal-api';
import { orderApi } from '../api/hauItdeSparepartsPurchaseNonshopPortalApi';
import { formatCurrency } from '../helper/currency';
import { getLocalization } from '../localization/localizationService';
import { useDataStore } from './data';

export type ExtendedOrderPosition = OrderPosition & {
  price?: number;
  currency?: string;
  stock?: string;
  stockType?: string;
  stockText?: string;
  position: number;
};

export const useOrderStore = defineStore('orderStore', () => {
  //helper vars
  const _orderMap = {
    AVAILABLE: 1,
    NOT_AVAILABLE: 2,
    NOT_AVAILABLE_LONGTERM: 3
  };

  //-------State------------------------------------------------
  const orders = ref<Array<ExtendedOrderPosition>>([]);
  const orderHead = ref<SparePartOrder>({
    city: '',
    comment: '',
    companyName: '',
    email: '',
    firstName: '',
    houseNumber: '',
    lastName: '',
    locale1: '',
    orderPositions: orders.value,
    phoneNumber: '',
    serialNumber: '',
    streetName: '',
    zipCode: ''
  });
  const dataStore = useDataStore();

  //-------ACTIONS----------------------------------------------------------
  const addArticle = (articlePosition: ExtendedOrderPosition) => {
    //--------VIRTUAL GROUP--------
    const { getZPartsByPosition, getVirtualElementsWithoutGroups } = dataStore;
    if (!!articlePosition.position) {
      const positionsByVirtualGroup = getVirtualElementsWithoutGroups(
        articlePosition.position
      );

      if (positionsByVirtualGroup.length > 0) {
        positionsByVirtualGroup.forEach((article) => {
          //check is in cart
          const virtualPosition = orders.value.find(
            (x) =>
              x.articleNumber === article.articleNumber &&
              x.position === articlePosition.position
          );

          if (virtualPosition !== undefined) {
            virtualPosition.qty =
              (virtualPosition.qty || 1) +
              (articlePosition?.qty || 1) * (article.quantity || 1);
          } else {
            orders.value.push({
              articleName: article.name,
              articleNumber: article.articleNumber,
              qty: (article.quantity || 1) * (articlePosition?.qty || 1),
              price: article.priceGross || 0,
              currency: article.currency,
              stockText: article.stockText,
              stockType: article.stockType,
              position: articlePosition.position
            } as ExtendedOrderPosition);
          }
        });

        return;
      }
    }

    //----------------NORMAL ARTICLE AND Z-PARTS----------------------------------
    //check is in cart
    const orderPosition = orders.value.find(
      (x) =>
        x.articleNumber === articlePosition.articleNumber &&
        x.position === articlePosition.position
    );
    if (orderPosition !== undefined) {
      orderPosition.qty = (orderPosition.qty || 1) + (articlePosition.qty || 1);
    } else {
      orders.value.push(articlePosition);
    }

    //Check z-part
    if (!!articlePosition.position) {
      const zParts = getZPartsByPosition(articlePosition.position).value;

      zParts.forEach((zPart) => {
        //check ist in cart
        const zPartPosition = orders.value.find(
          (x) =>
            x.articleNumber === zPart.articleNumber &&
            x.position === articlePosition.position
        );
        if (zPartPosition !== undefined) {
          zPartPosition.qty =
            (zPartPosition.qty || 1) +
            (articlePosition?.qty || 1) * (zPart.quantity || 1);
        } else {
          orders.value.push({
            articleName: zPart.name,
            articleNumber: zPart.articleNumber,
            qty: (zPart.quantity || 1) * (articlePosition?.qty || 1),
            price: zPart.priceGross || 0,
            currency: zPart.currency,
            stockText: zPart.stockText,
            stockType: zPart.stockType,
            position: articlePosition.position
          } as ExtendedOrderPosition);
        }
      });
    }
  };

  const sendFormular = () => {
    const { serialnumber } = storeToRefs(dataStore);
    orderHead.value.orderPositions = orders.value;
    orderHead.value.locale1 = getLocalization().value.Locale;
    orderHead.value.serialNumber = serialnumber.value;

    return orderApi.v1OrderPost(orderHead.value).catch((err) => {
      //throw to catch error in frontend and set errorOccured
      throw err;
    });
  };

  const getSum = () => {
    let sumByCurrency = new Map<string | 'default', number>();
    let sum: Array<string> = [];
    orders.value.forEach((article) => {
      sumByCurrency.set(
        article.currency || 'default',
        (sumByCurrency.get(article.currency || 'default') || 0) +
          (article.price || 0) * (article.qty || 1)
      );
    });

    sumByCurrency.forEach((_sumByCurrency, currency) => {
      sum.push(
        formatCurrency(_sumByCurrency, currency === 'default' ? null : currency)
      );
    });

    return sum.join(' + ');
  };

  //---------MUTATATIONS------------------------------------------------------
  const clearOrders = () => {
    orders.value = [];
  };

  const clearCompleteOrder = () => {
    orderHead.value = {};
  };

  const removeOrder = (index: number) => {
    const position = orders.value[index].position;

    orders.value = orders.value.filter((x) => x.position !== position);
  };

  const resetOrders = () => {
    orders.value = [];
  };

  //--------------------Getters----------------------------------------------
  const getItemNamesUnique = () => {
    return computed(() => {
      return orders.value
        .map((x) => x.articleName)
        .filter((value, index, array) => array.indexOf(value) === index)
        .join(', ');
    });
  };

  type order = typeof _orderMap;
  const getSummaryStock = () => {
    return computed(() => {
      const ordered = [...orders.value].sort(
        (a, b) =>
          _orderMap[(b.stockType ?? 'AVAILABLE') as keyof order] -
          _orderMap[(a.stockType ?? 'AVAILABLE') as keyof order]
      );

      return {
        stockText: ordered[0].stockText ?? '',
        stockType: ordered[0].stockType ?? ''
      };
    });
  };

  const getSummaryStockType = () => {
    return computed(() => {
      return getSummaryStock().value.stockType;
    });
  };

  const getSummaryStockText = () => {
    return computed(() => {
      return getSummaryStock().value.stockText;
    });
  };

  return {
    orders,
    orderHead,
    addArticle,
    sendFormular,
    getSum,
    clearOrders,
    getSummaryStockType,
    getSummaryStockText,
    getItemNamesUnique,
    removeOrder,
    clearCompleteOrder,
    resetOrders
  };
});
